import { useMemo, useState } from "react";
import { Checkbox } from "@chakra-ui/react";
import { FiX, FiSearch } from "react-icons/fi";
import { SelectWithLabel, CompactTextInput } from "newui/components/ui/InputWithLabel";
import { ToggleButtonGroup } from "newui/components/ui/ToggleButtonGroup";
import { Button } from "newui/components/Button";
import { TemplateQuickAssign } from "../TemplateQuickAssign";
import type {
	Role,
	RoleTemplate,
	UserAssignment,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export type AssignMode = "role" | "template";

// Users are held client-side (getUserAssignments returns the full set), so search
// and paging are client-side. Render in pages of PAGE_SIZE to keep large companies
// (100s of users) responsive rather than mounting every row at once.
const PAGE_SIZE = 25;

interface Step1UserRolePickerProps {
	users: UserAssignment[];
	selectedUserIds: number[];
	onToggleUser: (userId: number) => void;
	// Bulk set selection for a list of user ids (used by the filter-aware select-all).
	onSetUsersSelected: (userIds: number[], selected: boolean) => void;
	mode: AssignMode;
	onModeChange: (mode: AssignMode) => void;
	roles: Role[];
	selectedRoleId: number | null;
	onSelectRole: (roleId: number | null) => void;
	templates: RoleTemplate[];
	selectedTemplateId: number | null;
	onSelectTemplate: (id: number) => void;
}

const initials = (name: string): string =>
	name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

// Step 1: multi-select user checkboxes (row-selection pattern) + role OR template choice.
export const Step1UserRolePicker = ({
	users,
	selectedUserIds,
	onToggleUser,
	onSetUsersSelected,
	mode,
	onModeChange,
	roles,
	selectedRoleId,
	onSelectRole,
	templates,
	selectedTemplateId,
	onSelectTemplate,
}: Step1UserRolePickerProps) => {
	const [query, setQuery] = useState("");
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	const selectedUsers = users.filter((user) => selectedUserIds.includes(user.id));

	const isFiltering = query.trim().length > 0;
	const filteredUsers = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) {
			return users;
		}
		return users.filter(
			(user) =>
				user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q)
		);
	}, [users, query]);

	const visibleUsers = filteredUsers.slice(0, visibleCount);
	const hasMore = filteredUsers.length > visibleCount;

	// Select-all operates on the CURRENT filter result, so searching then "select all"
	// only affects the matches (not the whole company).
	const selectedInFiltered = filteredUsers.filter((user) =>
		selectedUserIds.includes(user.id)
	).length;
	const allFilteredSelected =
		filteredUsers.length > 0 && selectedInFiltered === filteredUsers.length;
	const someFilteredSelected = selectedInFiltered > 0 && !allFilteredSelected;

	const handleSearchChange = (value: string) => {
		setQuery(value);
		setVisibleCount(PAGE_SIZE);
	};

	const handleToggleAllFiltered = () => {
		onSetUsersSelected(
			filteredUsers.map((user) => user.id),
			!allFilteredSelected
		);
	};

	const roleOptions = roles.map((role) => ({ label: role.name, value: role.id }));
	const selectedRoleOption =
		roleOptions.find((option) => option.value === selectedRoleId) ?? null;

	return (
		<div data-testid="wizard-step1">
			<p className="mb-2 text-[11.5px] font-bold uppercase tracking-wide text-gray-600">
				Step 1 — Pick user(s)
			</p>

			<div className="relative mb-2">
				<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
				<CompactTextInput
					type="text"
					value={query}
					onChange={(event) => handleSearchChange(event.target.value)}
					placeholder="Search users by name or email…"
					inputClassName="pl-9"
					aria-label="Search users by name or email"
					data-testid="user-search-input"
				/>
			</div>

			<div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2">
				<Checkbox
					isChecked={allFilteredSelected}
					isIndeterminate={someFilteredSelected}
					onChange={handleToggleAllFiltered}
					data-testid="user-select-all"
				>
					<span className="text-sm text-gray-600">
						{isFiltering
							? `${selectedInFiltered} of ${filteredUsers.length} matching selected`
							: `${selectedUserIds.length} of ${users.length} selected`}
					</span>
				</Checkbox>
			</div>

			<div className="max-h-56 overflow-auto rounded-md border border-gray-200" data-testid="user-pick-list">
				{filteredUsers.length === 0 ? (
					<p className="px-3 py-6 text-center text-sm text-gray-500" data-testid="user-pick-empty">
						No users match “{query.trim()}”.
					</p>
				) : (
					visibleUsers.map((user) => {
						const checked = selectedUserIds.includes(user.id);
						return (
							<div
								key={user.id}
								className="flex items-center gap-2.5 border-b border-gray-100 px-3 py-2 last:border-b-0 hover:bg-blue-50"
							>
								<Checkbox
									isChecked={checked}
									onChange={() => onToggleUser(user.id)}
									aria-label={`Select ${user.name}`}
									data-testid={`user-pick-${user.id}`}
								/>
								<span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[10.5px] font-semibold text-blue-700">
									{initials(user.name)}
								</span>
								<span className="text-sm font-medium">{user.name}</span>
								<span className="text-xs text-gray-500">{user.email}</span>
							</div>
						);
					})
				)}
			</div>

			{hasMore && (
				<div className="mt-2 flex justify-center">
					<Button
						btntype="plain-border"
						size="xs"
						onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
						data-testid="user-pick-show-more"
					>
						Show {Math.min(PAGE_SIZE, filteredUsers.length - visibleCount)} more
						{" "}({filteredUsers.length - visibleCount} remaining)
					</Button>
				</div>
			)}

			{selectedUsers.length > 0 && (
				<div className="mt-3 flex flex-wrap gap-2" data-testid="selected-user-chips">
					{selectedUsers.map((user) => (
						<span
							key={user.id}
							className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700"
						>
							{user.name}
							<button
								type="button"
								onClick={() => onToggleUser(user.id)}
								aria-label={`Remove ${user.name}`}
								data-testid={`selected-user-remove-${user.id}`}
								className="opacity-70 hover:opacity-100"
							>
								<FiX size={12} />
							</button>
						</span>
					))}
				</div>
			)}

			<div className="mt-5">
				<p className="mb-2 text-[11.5px] font-bold uppercase tracking-wide text-gray-600">
					Step 2 — Choose access
				</p>
				<ToggleButtonGroup
					options={[
						{ label: "Quick-assign template", value: "template" },
						{ label: "Single role", value: "role" },
					]}
					value={mode}
					onChange={(value) => onModeChange(value as AssignMode)}
				/>
			</div>

			{mode === "template" ? (
				<div className="mt-4">
					<TemplateQuickAssign
						templates={templates}
						selectedId={selectedTemplateId}
						onSelect={onSelectTemplate}
					/>
				</div>
			) : (
				<div className="mt-4 max-w-sm">
					<SelectWithLabel
						label="Role"
						required
						testId="role-select"
						options={roleOptions}
						value={selectedRoleOption}
						onChange={(option) =>
							onSelectRole(
								option && !Array.isArray(option) ? (option.value as number) : null
							)
						}
						placeholder="Select a role…"
					/>
				</div>
			)}
		</div>
	);
};
